/**
 * Where dictated words land in a field. Shared by every dictated field, on
 * the phone and on the website, so words are placed the same way everywhere.
 */
const instanceDictation = 'dictation:instance:';
const targetDictation = 'dictation:target:';
export function dictationRecordingPurpose(instanceId: string, targetId?: string): string {
  return targetId ? `${targetDictation}${targetId}` : `${instanceDictation}${instanceId}`;
}
export function isDictationRecordingPurpose(purpose: string): boolean {
  return purpose.startsWith(instanceDictation) || purpose.startsWith(targetDictation);
}
/** A stable destination owns its own recovery; abandoned transient fields become explicit capture decisions. */
export function recoversAsCapture(purpose: string): boolean {
  return purpose === 'capture' || purpose.startsWith(instanceDictation);
}

/**
 * How many characters `value` is past `max`, counted the way a text box counts
 * its own limit (UTF-16 code units); 0 when it fits or there is no limit.
 * Dictation never cuts words to fit a limit: the box keeps them, says how far
 * over it is, and the form's Save waits until the person has shortened it.
 */
export function charactersOver(value: string, max: number | undefined): number {
  return max === undefined ? 0 : Math.max(0, value.length - max);
}

/**
 * The longest a recording runs (CER-83, CER-251): at ten minutes it stops by itself
 * and is kept, as if the person had stopped it. A brain dump already runs this
 * long through the same transcription path.
 */
export const MAX_RECORDING_MS = 10 * 60 * 1000;

/** What is said when a recording was stopped at the limit. */
export const RECORDING_LIMIT_NOTE = 'Recordings stop at 10 minutes; this one was kept.';

/**
 * A recording also stops by itself after this long without speech, and is
 * kept (Chris, 2026-10-06: no real input stalls for over 30 seconds). It
 * ends a recording left running by mistake, and spares the transcriber a
 * long silence, where it tends to invent words.
 */
export const SILENCE_STOP_MS = 30 * 1000;

/**
 * The loudest a moment may peak, in dB below full scale, and still count as
 * silence. Speech into a phone or laptop microphone peaks far above it; a
 * quiet room stays below it. A loud room may never fall below it, and the
 * ten-minute limit still ends the recording.
 */
export const SILENCE_DBFS = -45;

/** What is said when a recording was stopped by silence. */
export const SILENCE_NOTE = 'Stopped after 30 seconds of silence; this one was kept.';

/**
 * Follows a recording's level as it is reported: `heard(atMs, peakDbfs)`
 * says whether it has now been silent for SILENCE_STOP_MS. Any louder moment
 * starts the count again.
 */
export function silenceWatch(stopMs = SILENCE_STOP_MS): (atMs: number, peakDbfs: number) => boolean {
  let quietSince: number | null = null;
  return (atMs, peakDbfs) => {
    if (peakDbfs > SILENCE_DBFS) {
      quietSince = null;
      return false;
    }
    quietSince ??= atMs;
    return atMs - quietSince >= stopMs;
  };
}

/**
 * A brain dump (CER-225 phase 1) runs longer and pauses longer than a
 * capture: up to ten minutes, ended only by a full minute of quiet, so a
 * person can walk the house and let things remind them (Chris, 2026-10-06).
 */
export const BRAIN_DUMP_MAX_MS = 10 * 60 * 1000;
export const BRAIN_DUMP_SILENCE_MS = 60 * 1000;
export const BRAIN_DUMP_LIMIT_NOTE = 'Brain dumps stop at 10 minutes; this one was kept.';
export const BRAIN_DUMP_SILENCE_NOTE = 'Stopped after a minute of quiet; this one was kept.';

/** A cursor or selection in the field, as the caller last saw it. */
export interface FieldSelection {
  start: number;
  end: number;
}

/**
 * `value` with `transcript` added. At a cursor or selection (`at`), the words
 * replace the selection, spaced from the text on either side; with no cursor
 * placed, they start a new line after what is there. `cursor` is where the
 * cursor belongs afterwards: just after the words.
 */
export function withTranscript(
  value: string,
  transcript: string,
  at: FieldSelection | null,
): { text: string; cursor: number } {
  if (!at) {
    const separator = value && !value.endsWith('\n') ? '\n' : '';
    const text = `${value}${separator}${transcript}`;
    return { text, cursor: text.length };
  }
  const before = value.slice(0, at.start);
  const after = value.slice(at.end);
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  const trail = after && !/^\s/.test(after) ? ' ' : '';
  const inserted = `${lead}${transcript}${trail}`;
  return { text: `${before}${inserted}${after}`, cursor: before.length + lead.length + transcript.length };
}
